import { decryptCBCResponse } from "./decryptApi";

const BASE_URL = process.env.REACT_APP_HAPS_MAIN_BASE_URL || "http://localhost:8080/";

export interface ApiCallParams {
  endpoint: string;
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  data?: any | FormData | null;
  headers?: Record<string, string>;
  params?: Record<string, string> | null;
}

export interface ApiResponse<T = any> {
  status: number;
  message?: string;
  data: T;
  success?: boolean;
}

/**
 * Global API caller
 */
export const apiCall = async <T = any>({
  endpoint,
  method = "GET",
  data = null,
  headers = {},
  params = null,
}: ApiCallParams): Promise<ApiResponse<T>> => {
  let url = `${BASE_URL}${endpoint}`;

  if (params) {
    url += `?${new URLSearchParams(params).toString()}`;
  }

  const token = localStorage.getItem("Token");

  // 🔍 Detect FormData
  const isFormData = data instanceof FormData;
  const isUrlEncoded = data instanceof URLSearchParams;
  const isStringBody = typeof data === "string";
  const headerKeys = Object.keys(headers || {}).map((key) =>
    key.toLowerCase()
  );
  const hasContentType = headerKeys.includes("content-type");
  const config: RequestInit = {
    method,
    headers: {
      ...(isFormData || hasContentType
        ? {}
        : isUrlEncoded
        ? { "Content-Type": "application/x-www-form-urlencoded" }
        : { "Content-Type": "application/json" }),
      ...headers,
    },
  };

  if (token) {
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }

  if (data && method !== "GET") {
    if (isFormData) {
      config.body = data;
    } else if (isUrlEncoded) {
      config.body = data.toString();
    } else if (isStringBody) {
      config.body = data;
    } else {
      config.body = JSON.stringify(data);
    }
  }

  try {
    const response = await fetch(url, config);
    const json = await response.json();
    const result = decryptCBCResponse(json);
    if (process.env.NODE_ENV === "development") {
      // Use debug-level logging in development only
      // to avoid noisy logs in production consoles.
      // eslint-disable-next-line no-console
      console.debug("API Response:", result);
    }

    // The decrypted response contains the actual status
    // Return it as-is, whether success or error
    return result as ApiResponse<T>;
  } catch (err: any) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.error("API Error:", decryptCBCResponse(err?.response?.data) || err);
    }
    // Return error object instead of throwing

    return decryptCBCResponse(err?.response?.data) || {
      status: 500,
      message: err?.message || "Network or parsing error",
      data: null,
    };
  }
};
